import axios from 'axios'
import type {
  AdvanceResponse,
  ConnectResponse,
  DeclineResponse,
  Language,
  ResolveOutcome,
  ResolvedSessionCreated,
  SelectResponse,
} from '@/types/join'

// Standalone axios instance — no auth-store interceptor.
const httpClient = axios.create({
  // Same-origin: SPA + API live behind the same Caddy in stage 1.
  // If the API moves to a different origin, set VITE_PUBLIC_API_BASE_URL.
  baseURL: import.meta.env.VITE_PUBLIC_API_BASE_URL ?? '',
  // Don't auto-throw on 4xx — we map status codes to ResolveOutcome variants.
  validateStatus: () => true,
})


export async function resolveSession(
  sessionId: string,
  token: string,
): Promise<ResolveOutcome> {
  try {
    const res = await httpClient.get(`/v1/public/sessions/${sessionId}`, {
      params: { t: token },
    })
    if (res.status === 200) {
      return { kind: 'created', data: res.data as ResolvedSessionCreated }
    }
    if (res.status === 410) {
      return { kind: 'terminal', data: res.data }
    }
    if (res.status === 409) {
      return { kind: 'in_progress' }
    }
    if (res.status === 404) {
      return { kind: 'not_found' }
    }
    return { kind: 'network_error', message: `Unexpected status ${res.status}` }
  } catch (err) {
    return {
      kind: 'network_error',
      message: err instanceof Error ? err.message : String(err),
    }
  }
}


function authHeader(bearer: string): { Authorization: string } {
  return { Authorization: `Bearer ${bearer}` }
}


export async function connectSession(
  sessionId: string, bearer: string, connectionId: string,
): Promise<ConnectResponse> {
  const res = await httpClient.post(
    `/v1/public/sessions/${sessionId}/connect`,
    { connectionId },
    { headers: authHeader(bearer) },
  )
  if (res.status !== 200) throw new PublicSessionError(res.status, res.data)
  return res.data as ConnectResponse
}


export async function declineSession(
  sessionId: string, bearer: string,
): Promise<DeclineResponse> {
  const res = await httpClient.post(
    `/v1/public/sessions/${sessionId}/decline`,
    {},
    { headers: authHeader(bearer) },
  )
  if (res.status !== 200) throw new PublicSessionError(res.status, res.data)
  return res.data as DeclineResponse
}


export async function advanceSegment(
  sessionId: string, bearer: string, connectionId: string,
): Promise<AdvanceResponse> {
  const res = await httpClient.post(
    `/v1/public/sessions/${sessionId}/advance`,
    { connectionId },
    { headers: authHeader(bearer) },
  )
  if (res.status !== 200) throw new PublicSessionError(res.status, res.data)
  return res.data as AdvanceResponse
}


export async function selectSession(
  sessionId: string,
  bearer: string,
  body: { selectedLanguage: Language; selectedAvatarIndex: number },
): Promise<SelectResponse> {
  const res = await httpClient.post(
    `/v1/public/sessions/${sessionId}/select`,
    body,
    { headers: authHeader(bearer) },
  )
  if (res.status !== 200) throw new PublicSessionError(res.status, res.data)
  return res.data as SelectResponse
}


// Issue #22 Phase 2 — pull fallback for queue position when the LiveKit
// data channel isn't connected yet (or dropped).
export interface QueueStatusResponse {
  state: 'queued' | 'not_queued'
  position: number | null
  etaSeconds: number | null
  queueDepth: number
}

export async function getQueueStatus(
  sessionId: string,
  bearer: string,
): Promise<QueueStatusResponse> {
  const res = await httpClient.get(
    `/v1/public/sessions/${sessionId}/queue-status`,
    { headers: authHeader(bearer) },
  )
  if (res.status !== 200) throw new PublicSessionError(res.status, res.data)
  return res.data as QueueStatusResponse
}


export class PublicSessionError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`Public session call failed: HTTP ${status}`)
  }
}

// Keepalive also works during page departure; never wait for media cleanup first.
export async function abortSession(sessionId: string, bearer: string, connectionId: string): Promise<{ status: string }> {
  const response = await fetch(
    (import.meta.env.VITE_PUBLIC_API_BASE_URL ?? '') + '/v1/public/sessions/' + sessionId + '/abort',
    {
      method: 'POST',
      headers: { ...authHeader(bearer), 'Content-Type': 'application/json' },
      body: JSON.stringify({ connectionId }),
      keepalive: true,
    },
  )
  if (!response.ok) throw new Error('Interview abort failed: ' + response.status)
  return response.json()
}
